import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Amount } from "@/components/Currency";
import { Btn, Card, Empty, Field, Input, Loading, Pill, Select, Table, Textarea } from "@/components/Ui";

export default function PurchaseHistory() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/buyer/purchase-history");
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (r) => {
    setActive(r);
    setForm({
      quantity: r.quantity || 1,
      scope: "same",
      required_delivery_date: "",
      closing_date: "",
      delivery_location: r.delivery_location || "",
      notes: "",
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/buyer/purchase-history/${active.award_id}/reorder`, {
        quantity: Number(form.quantity),
        scope: form.scope,
        required_delivery_date: form.required_delivery_date || null,
        closing_date: form.closing_date || null,
        delivery_location: form.delivery_location || null,
        notes: form.notes || null,
      });
      toast.success(`${data.rfq_number} sent to ${data.matched_suppliers} supplier(s)`);
      setActive(null);
      navigate("/buyer/rfqs");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <Loading />;
  if (!rows.length) {
    return <Empty testId="purchase-history-empty" title="No purchase history yet"
      sub="Once you award an enquiry, the order appears here and can be reordered in one click." />;
  }

  return (
    <div data-testid="purchase-history">
      <Card className="bg-[#F5F7FA]">
        <p className="text-sm text-[#334e68]">
          Every awarded order, with the price and lead time you last paid. Reorder sends a
          pre-filled enquiry — to the same supplier, or to all matched suppliers if you want to
          compare prices again.
        </p>
      </Card>

      <Table testId="purchase-history-table"
        head={["Product", "Previous supplier", "Previous price", "Qty", "Order date", "Lead time", ""]}>
        {rows.map((r) => (
          <tr key={r.award_id} data-testid={`history-row-${r.award_id}`}>
            <td>
              <p className="font-semibold text-[#102A43]">{r.part_number || r.title}</p>
              <p className="text-xs text-[#8598ac]">{r.manufacturer || r.product_name || r.rfq_number}</p>
            </td>
            <td>
              {r.supplier_company_name}
              <p className="text-xs text-[#8598ac]">{r.order_number || r.rfq_number}</p>
            </td>
            <td>
              <Amount value={r.unit_price} currency={r.currency} block />
              <p className="text-xs text-[#8598ac]">per unit</p>
            </td>
            <td>{r.quantity} {r.unit}</td>
            <td>{(r.order_date || "").slice(0, 10)}</td>
            <td>{r.lead_time_days != null ? `${r.lead_time_days} days` : "—"}</td>
            <td>
              <div className="flex flex-wrap justify-end gap-2">
                <Btn variant="gold" className="px-3 py-2 text-xs"
                  data-testid={`reorder-btn-${r.award_id}`} onClick={() => open(r)}>
                  <RotateCcw className="h-4 w-4" /> Reorder
                </Btn>
              </div>
            </td>
          </tr>
        ))}
      </Table>

      {active && (
        <Card className="mt-5" data-testid="reorder-panel">
          <h2 className="font-display text-lg font-black tracking-tight text-[#102A43]">
            Reorder {active.part_number}
          </h2>
          <p className="mt-1 text-sm text-[#667788]">
            Pre-filled from {active.order_number || active.rfq_number} — specifications and terms
            carried over from the original enquiry.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="Quantity *">
              <Input data-testid="reorder-quantity" type="number" min="1" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Field>
            <Field label="Required by">
              <Input data-testid="reorder-required-date" type="date" value={form.required_delivery_date}
                onChange={(e) => setForm({ ...form, required_delivery_date: e.target.value })} />
            </Field>
            <Field label="Quotes close on">
              <Input data-testid="reorder-closing-date" type="date" value={form.closing_date}
                onChange={(e) => setForm({ ...form, closing_date: e.target.value })} />
            </Field>
            <Field label="Delivery location" className="sm:col-span-2">
              <Input data-testid="reorder-location" value={form.delivery_location}
                onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} />
            </Field>
            <Field label="Who should quote? *">
              <Select data-testid="reorder-scope" value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                options={[["same", `${active.supplier_company_name} only`],
                          ["all", "Same supplier + all other matched suppliers"]]} />
            </Field>
            <Field label="Notes to suppliers" className="sm:col-span-3">
              <Textarea data-testid="reorder-notes" rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Btn data-testid="reorder-submit-btn" loading={busy} onClick={submit}>
              <Send className="h-4 w-4" /> Send reorder enquiry
            </Btn>
            <Btn variant="ghost" data-testid="reorder-cancel-btn" onClick={() => setActive(null)}>
              Cancel
            </Btn>
            <Pill tone="neutral">
              Last paid <Amount value={active.unit_price} currency={active.currency} /> per unit
            </Pill>
          </div>
        </Card>
      )}
    </div>
  );
}
